import { useEffect, useState } from "react";
import { Eye, MousePointerClick } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, fmtDate } from "@/lib/api";
import { StatusBadge } from "./StatusBadge";

export function HistoryPanel({ slot }) {
  const [rows, setRows] = useState([]);
  const [camps, setCamps] = useState([]);
  const [cid, setCid] = useState("all");
  const [status, setStatus] = useState("all");

  useEffect(() => { api.get(`/projects/${slot}/campaigns`).then((r) => setCamps(r.data)); }, [slot]);
  useEffect(() => {
    api.get(`/projects/${slot}/sends`, { params: { campaign_id: cid === "all" ? "" : cid, status: status === "all" ? "" : status } })
      .then((r) => setRows(r.data));
  }, [slot, cid, status]);

  return (
    <div className="space-y-4" data-testid="history-panel">
      <div className="flex flex-wrap gap-3">
        <Select value={cid} onValueChange={setCid}>
          <SelectTrigger className="w-64" data-testid="history-campaign-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All campaigns</SelectItem>
            {camps.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40" data-testid="history-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem><SelectItem value="sent">Sent</SelectItem><SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="delivered">Delivered</SelectItem><SelectItem value="opened">Opened</SelectItem><SelectItem value="clicked">Clicked</SelectItem>
            <SelectItem value="bounced">Bounced</SelectItem><SelectItem value="complained">Spam report</SelectItem><SelectItem value="delivery_delayed">Delayed</SelectItem>
          </SelectContent>
        </Select>
        <span className="ml-auto text-xs font-mono text-muted-foreground self-center" data-testid="history-count">{rows.length} records</span>
      </div>
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Recipient</TableHead><TableHead>Campaign</TableHead><TableHead>Subject</TableHead>
              <TableHead>Sent</TableHead><TableHead>Status</TableHead><TableHead>Delivery</TableHead><TableHead>Engagement</TableHead><TableHead>Resend ID / error</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-14 text-muted-foreground" data-testid="history-empty">No emails sent yet.</TableCell></TableRow>}
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`history-row-${r.id}`}>
                <TableCell><p className="font-mono text-xs">{r.email}</p><p className="text-xs text-muted-foreground">{r.name}</p></TableCell>
                <TableCell className="text-sm">{r.campaign_name}</TableCell>
                <TableCell className="text-sm max-w-[260px] truncate">{r.subject}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{fmtDate(r.sent_at)}</TableCell>
                <TableCell><StatusBadge status={r.status} /></TableCell>
                <TableCell>
                  {r.status === "sent" ? <StatusBadge status={r.delivery || "sent"} testId={`history-delivery-${r.id}`} /> : "—"}
                  {r.bounce_reason && <p className="text-[11px] text-rose-700 mt-1 max-w-[180px] truncate" title={r.bounce_reason}>{r.bounce_reason}</p>}
                </TableCell>
                <TableCell className="text-xs" data-testid={`history-engagement-${r.id}`}>
                  <span className={`inline-flex items-center gap-1 mr-3 ${r.opened_at ? "text-indigo-700" : "text-muted-foreground/50"}`} title={r.opened_at ? `First opened ${fmtDate(r.opened_at)}` : "Not opened"}>
                    <Eye className="h-3.5 w-3.5" />{r.open_count || 0}
                  </span>
                  <span className={`inline-flex items-center gap-1 ${r.clicked_at ? "text-indigo-700" : "text-muted-foreground/50"}`} title={r.last_click_url || "No clicks"}>
                    <MousePointerClick className="h-3.5 w-3.5" />{r.click_count || 0}
                  </span>
                </TableCell>
                <TableCell className="font-mono text-[11px] text-muted-foreground max-w-[260px] truncate" title={r.error || r.resend_id}>{r.error || r.resend_id || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
